import {
  AutocompleteInput,
  borderRadius,
  Button,
  customScrollMinimal,
  Dropdown,
  DropdownList,
  DropdownProps,
  Icon,
  Input,
  Modal,
  typographyV2,
} from '@toggle/design-system';
import { JSX } from 'react';
import styled, { css } from 'styled-components';

export const StyledModal = styled(Modal)<{
  $inChartPage?: boolean;
  $searchInputVariant?: 'default' | 'secondary';
  $showHeader?: boolean;
}>`
  ${Modal.Styled.TextContainer} {
    margin-top: 12px;
    margin-bottom: 0;
  }

  ${({ $inChartPage, $searchInputVariant, $showHeader = true }) => {
    const isSecondaryVariant = $searchInputVariant === 'secondary';

    return css`
      ${Modal.Styled.Header} {
        ${!$showHeader &&
        css`
          display: none;
        `}
      }

      ${Modal.Styled.ModalContainer} {
        background-color: var(--background-soft);
        display: flex;
        flex-direction: column;
        min-height: ${isSecondaryVariant ? '520px' : '460px'};
        width: ${$inChartPage ? '1000px' : '792px'};
        box-shadow: var(--elevation-moderate);
        padding: 0;

        ${isSecondaryVariant &&
        css`
          top: calc(var(--app-header-height) + 12px);
          transform: translateX(-50%);
          background-color: var(--background-soft);
        `}
      }

      &${Modal.Styled.Container} {
        top: ${$inChartPage ? 'var(--app-header-height)' : '0px'};
      }

      ${Modal.Styled.Backdrop} {
        background-color: ${$inChartPage
          ? 'transparent'
          : 'var(--background-backdrop)'};
        backdrop-filter: ${$inChartPage ? 'blur(0px)' : 'blur(1px)'};
        top: ${$inChartPage ? 'var(--app-header-height)' : '0'};
      }
    `;
  }}
`;

export const SearchResultWrapper = styled.div`
  flex-shrink: 0;
  width: 600px;
`;

export const StyledDropdown = styled(Dropdown)<{
  $isSecondaryVariant?: boolean;
}>`
  padding: 0px;

  ${Dropdown.Styled.DropdownListContainer} {
    position: relative;
    overflow-x: hidden;
  }
  ${DropdownList.Styled.DropdownListRoot} {
    box-shadow: none;
    border: none;
    background-color: unset;
    padding: 0px 8px 8px 8px;
    min-height: ${({ $isSecondaryVariant }) =>
      $isSecondaryVariant ? '400px' : '300px'};
  }
` as <T>(
  props: DropdownProps<T> & { $isSecondaryVariant?: boolean }
) => JSX.Element;

export const ModalContent = styled.div`
  display: flex;
  height: 460px;
  width: 1000px;
`;

export const VerticalDivider = styled.div`
  margin-left: 16px;
  margin-right: 8px;
  border-left: 1px solid var(--border-soft);
  height: 100%;
`;

export const HelpText = styled.p`
  ${typographyV2.bodySm};
  color: var(--text-soft);
  padding: 12px 16px;
`;

export const ClearButton = styled.button`
  ${typographyV2.bodySm};
  color: var(--text-soft);
  cursor: pointer;
  border: none;
  background: none;

  &:hover {
    color: var(--text-default);
  }
`;

export const StyledAutocompleteInput = styled(AutocompleteInput)<{
  $autocompleteInputVariant?: 'default' | 'secondary';
  $showHeader?: boolean;
}>`
  ${Input.Styles.LeftIcon} svg {
    fill: var(--text-soft);
  }

  ${Input.Styles.Container} {
    width: 100%;
  }

  ${Input.Styles.InputWrapper} {
    border: 1px solid var(--border-soft);
    ${({ $autocompleteInputVariant, $showHeader = true }) =>
      $autocompleteInputVariant === 'secondary' &&
      css`
        border-left: none;
        border-right: none;
        ${$showHeader
          ? css`
              border-radius: 0;
            `
          : css`
              border-top: none;
              border-radius: 8px 8px 0 0;
            `}
      `}

    background-color: transparent;
    padding-left: 20px;
  }

  ${Input.Styles.InputElement} {
    text-align: left;
  }
`;

export const PanesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  max-height: 450px;
  overflow-y: scroll;
  width: 361px;
  ${customScrollMinimal};
`;

export const TextWrapper = styled.div`
  padding-left: 8px;
  padding-right: 8px;
`;

export const HelperText = styled.p`
  border: 1px dashed var(--border-soft);
  ${borderRadius.r2};
  display: flex;
  justify-content: center;
  padding: 12px;
  width: 100%;
  ${typographyV2.bodyXs};
  color: var(--text-soft);
`;

export const StyledButton = styled(Button)`
  svg {
    fill: var(--icon-soft);
  }
`;

export const BeforeResultsWrapper = styled.div<{
  $hasRoundedCorners?: boolean;
}>`
  ${({ $hasRoundedCorners }) =>
    $hasRoundedCorners &&
    css`
      border-radius: 8px 8px 0 0;
      overflow: hidden;
    `}
`;

export const TopBarSearchContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: var(--app-header-height);
  z-index: calc(var(--z-index-modal) + 1);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--background-soft);
  padding: 12px 16px;
  pointer-events: none;
`;

export const TopBarSearchInput = styled(AutocompleteInput)`
  pointer-events: auto;
  width: 100%;
  max-width: 600px;

  ${Input.Styles.LeftIcon} svg {
    fill: var(--icon-default);
  }

  ${Input.Styles.Container} {
    width: 100%;
  }

  ${Input.Styles.InputWrapper} {
    border: 1px solid var(--border-soft);
    border-radius: 40px;
    background-color: var(--surface-default);
    padding-left: 12px;
    padding-right: 18px;
    height: 44px;

    &:focus-within {
      border-color: var(--border-default);
    }
  }

  ${Input.Styles.InputElement} {
    ${typographyV2.bodySm};
    text-align: left;
  }
`;

export const TopBarButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const TopBarClearButton = styled.button`
  ${typographyV2.uiXs};
  color: var(--text-soft);
  cursor: pointer;
  border: none;
  background: none;
  padding: 2px 0;

  &:hover {
    color: var(--text-default);
  }
`;

export const TopBarDivider = styled.div`
  width: 1px;
  height: 20px;
  background-color: var(--border-soft);
`;

export const TopBarCloseButton = styled(Icon)`
  cursor: pointer;
  fill: var(--text-soft);

  &:hover {
    fill: var(--text-default);
  }
`;
